import { Button, ButtonProps, createStyles, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { SvgIconComponent } from '@material-ui/icons';
import classNames from 'classnames';
import React from 'react';

import { textSizes, TextSizesEnum } from '../../../../theme';
import { ButtonColor, buttonStyleColors } from './types';

export * from './types';

export interface PlexButtonProps extends Omit<ButtonProps, 'disabled' | 'color' | 'variant' | 'classes'> {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  label: string | number | JSX.Element;
  color?: ButtonColor;
  tooltip?: string;
  MuiIconLeft?: SvgIconComponent;
  MuiIconRight?: SvgIconComponent;
  SvgIconLeft?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  SvgIconRight?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  className?: string;
  classNameLabelLeft?: string;
  disabled?: boolean;
}

export const PlexButton = React.memo((props: PlexButtonProps) => {
  const {
    onClick,
    label,
    MuiIconLeft,
    MuiIconRight,
    SvgIconLeft,
    SvgIconRight,
    className,
    classNameLabelLeft,
    disabled,
    tooltip,
    ...buttonProps
  } = props;
  const classes = useStyles(props);
  const buttonView = (
    <Button
      {...buttonProps}
      disabled={disabled}
      onClick={onClick}
      color={'primary'}
      variant={'contained'}
      classes={{
        root: classNames(classes.muiButtonRoot, className),
        containedPrimary: classes.muiButtonContainedPrimary,
        label: classes.muiButtonLabel,
      }}
    >
      {MuiIconLeft && <MuiIconLeft classes={{ root: classNames(classes.muiSvgIconRootLeft, classNameLabelLeft) }} />}
      {SvgIconLeft && <SvgIconLeft className={classNames(classes.svgIconRootLeft, classNameLabelLeft)} />}
      {label}
      {MuiIconRight && <MuiIconRight classes={{ root: classes.muiSvgIconRootRight }} />}
      {SvgIconRight && <SvgIconRight className={classes.svgIconRootRight} />}
    </Button>
  );
  if (!tooltip) {
    return buttonView;
  }
  return (
    <Tooltip title={tooltip}>{disabled ? <span className={classes.span}>{buttonView}</span> : buttonView}</Tooltip>
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    muiButtonRoot: ({ color = ButtonColor.Primary }: PlexButtonProps) => ({
      display: 'flex',
      backgroundColor: buttonStyleColors[color].background,
      flexDirection: 'row',
    }),
    muiButtonContainedPrimary: ({ color = ButtonColor.Primary }: PlexButtonProps) => ({
      '&:hover': {
        backgroundColor: buttonStyleColors[color].hover,
      },
      '&:active': {
        backgroundColor: buttonStyleColors[color].hover,
      },
    }),
    muiButtonLabel: ({ color = ButtonColor.Primary }: PlexButtonProps) => ({
      color: buttonStyleColors[color].label,
      fontSize: textSizes[TextSizesEnum.Normal].fontSize,
      lineHeight: '24px',
      height: '100%',
    }),
    muiSvgIconRootLeft: {
      display: 'flex',
      marginRight: 12,
      width: 18,
      height: 18,
    },
    muiSvgIconRootRight: {
      display: 'flex',
      marginLeft: 12,
      width: 18,
      height: 18,
    },
    svgIconRootLeft: ({ color = ButtonColor.Primary }: PlexButtonProps) => ({
      display: 'flex',
      marginRight: 12,
      width: 24,
      height: 24,
      fill: `${buttonStyleColors[color].label} !important`, // sets the svg color
    }),
    svgIconRootRight: ({ color = ButtonColor.Primary }: PlexButtonProps) => ({
      display: 'flex',
      marginLeft: 12,
      width: 24,
      height: 24,
      fill: `${buttonStyleColors[color].label} !important`, // sets the svg color
    }),
    span: {
      display: 'flex',
    },
  })
);
